import { getRouteApi } from "@tanstack/react-router";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Phone,
  Check,
  Sparkles,
  Target,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import {
  getIndustryBySlug,
  INDUSTRY_SOLUTIONS,
  type IndustrySolution,
} from "@/data/industry-solutions";
import { SERVICES, type ServiceId } from "@/data/services";
import { SERVICE_HEAD, type ServiceHeadMeta } from "@/data/services-head.gen";

const routeApi = getRouteApi("/industries/$slug/$service/");

export function IndustryServicePage() {
  const { ind, service: sHead } = routeApi.useLoaderData() as {
    ind: IndustrySolution;
    service: ServiceHeadMeta;
  };
  const s = SERVICES[sHead.id];

  // Derive the matched solution detail (if any)
  const matchedSolution = ind.solutions.find((sol) => sol.serviceId === s.id);
  const otherServices = ind.relatedServiceIds.filter((id) => id !== s.id);

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      {/* Breadcrumb */}
      <nav className="border-b border-white/5 bg-[#0a1a3d] px-4 py-3 text-xs">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-1.5 text-white/80">
          <Link to="/" className="hover:text-amber-300">
            หน้าแรก
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/industries" className="hover:text-amber-300">
            Industries
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/industries/$slug" params={{ slug: ind.slug }} className="hover:text-amber-300">
            {ind.name}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-amber-300">{s.nameTh}</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14 sm:py-20">
        <div className="mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold text-amber-200">
            <Sparkles className="h-3 w-3" /> {ind.tagline} · Specialized Pack
          </div>
          <h1 className="aeo-speakable mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            {s.nameTh} <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              สำหรับ {ind.name}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-base text-white/80 sm:text-lg">
            {matchedSolution?.desc ?? s.taglineTh} — ออกแบบ workflow เฉพาะสำหรับ {ind.audience}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
                >
                  ขอใบเสนอราคา Custom · ฟรี <ArrowRight className="h-4 w-4" />
                </button>
              }
            />
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <Stat label="เริ่มต้น" value={`฿${s.priceFrom.toLocaleString()}`} />
            <Stat label="ระยะเวลา" value={s.turnaround} />
            <Stat label="ภาษา" value={`${s.languagesCount}+`} />
          </div>
        </div>
      </section>

      {/* Why this combo */}
      <section className="border-b border-white/10 px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300">
            ทำไม {ind.name} ต้องเลือก {s.nameTh}
          </div>
          <h2 className="aeo-speakable mt-2 text-2xl font-extrabold sm:text-3xl">
            ปัญหาที่เราแก้ให้คุณ
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {ind.painPoints.slice(0, 4).map((p, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="flex items-start gap-3">
                  <Target className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-300" />
                  <div>
                    <div className="text-sm font-bold text-white">{p.title}</div>
                    <p className="mt-1 text-sm leading-relaxed text-white/70">{p.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service highlights */}
      <section className="border-b border-white/10 bg-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="aeo-speakable text-2xl font-extrabold sm:text-3xl">สิ่งที่คุณจะได้รับ</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {s.purposesTh.slice(0, 6).map((purpose, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4"
              >
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-300" />
                <span className="text-sm text-white/85">{purpose}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-amber-400/20 bg-amber-500/[0.06] p-6">
            <div className="flex items-center gap-2 text-sm font-bold text-amber-200">
              <ShieldCheck className="h-4 w-4" /> Industry Compliance
            </div>
            <p className="mt-2 text-sm text-white/80">
              ทีม Account Manager เฉพาะสาย {ind.name} · เข้าใจ regulation ปลายทาง · ทำงานกับลูกค้าใน
              sector นี้ {ind.metrics[0]?.value ?? "100+"} ราย
            </p>
          </div>
        </div>
      </section>

      {/* Pricing snapshot */}
      <section className="border-b border-white/10 px-4 py-14">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">ราคาอ้างอิง</h2>
          <p className="mt-2 text-sm text-white/70">
            ราคาขั้นต่ำ — ลูกค้า {ind.name} ที่ใช้ retainer ลดสูงสุด 25%
          </p>
          <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.06] text-left text-xs uppercase tracking-wider text-white/80">
                <tr>
                  <th className="px-4 py-3">รายการ</th>
                  <th className="px-4 py-3 text-right">ราคา</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {s.prices.slice(0, 6).map((row, i) => (
                  <tr key={i} className="bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{row.item}</div>
                      {row.note && <div className="mt-0.5 text-xs text-white/80">{row.note}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-amber-200">{row.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Case study from industry */}
      <section className="border-b border-white/10 bg-[#0a1a3d] px-4 py-14">
        <div className="mx-auto max-w-4xl rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-500/[0.08] to-white/[0.02] p-8 sm:p-12">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300">
            Industry Case · {ind.name}
          </div>
          <h2 className="mt-2 text-2xl font-extrabold sm:text-3xl">{ind.caseStudy.client}</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">
                Challenge
              </p>
              <p className="mt-1.5 text-sm text-white/80">{ind.caseStudy.challenge}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                Result
              </p>
              <p className="mt-1.5 text-sm text-white/80">{ind.caseStudy.result}</p>
            </div>
          </div>
          <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Impact</p>
            <p className="mt-1 text-lg font-extrabold text-amber-100">{ind.caseStudy.metric}</p>
          </div>
        </div>
      </section>

      {/* Cross-link */}
      <section className="border-b border-white/10 px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-xl font-extrabold sm:text-2xl">บริการที่มักใช้คู่กันใน {ind.name}</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherServices.map((id) => {
              const o = SERVICES[id];
              if (!o) return null;
              return (
                <Link
                  key={id}
                  to="/industries/$slug/$service"
                  params={{ slug: ind.slug, service: id }}
                  className="group rounded-xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-amber-400/50 hover:bg-white/[0.07]"
                >
                  <div className="text-sm font-bold text-white group-hover:text-amber-200">
                    {o.nameTh}
                  </div>
                  <div className="mt-1 text-[11px] text-white/80">
                    ฿{o.priceFrom.toLocaleString()} · {o.turnaround}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-300">
                    ดู Pack สำหรับ {ind.name} <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
            <Link
              to="/services/$service"
              params={{ service: s.id }}
              className="group rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 transition hover:bg-amber-500/15"
            >
              <div className="text-sm font-bold text-amber-200">หน้าบริการหลัก {s.nameTh}</div>
              <div className="mt-1 text-[11px] text-white/70">ดูราคา · FAQ · กระบวนการครบ</div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-300">
                เปิดหน้าหลัก <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            พร้อมเริ่ม {s.nameTh} สำหรับ {ind.name}?
          </h2>
          <p className="mt-3 text-white/75">
            Dedicated Account Manager · SLA 24 ชม. · ใบเสนอราคาภายใน 15 นาที
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
                >
                  ขอใบเสนอราคา · ฟรี
                </button>
              }
            />
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">{label}</div>
      <div className="mt-1 text-xl font-extrabold text-white">{value}</div>
    </div>
  );
}
